import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { ImportExportComponent } from '@app/_components/_helpers/import-export/import-export.component';
import * as moment from 'moment';
import * as FileSaver from 'file-saver';

@Component({
	selector: 'app-escrow-report',
	templateUrl: './escrow-report.component.html',
	styleUrls: ['./escrow-report.component.css'],
	standalone: false,
	providers: [MessageService]
})
export class EscrowReportComponent implements OnInit {

	@ViewChild('escrowTable') escrowTable: Table;
	@ViewChild(ImportExportComponent) importExportComponent: ImportExportComponent;

	formLoading = false;

	// As-of date (default today, midnight)
	asOfDate: Date;
	recentDays = 30;

	// Summary tiles
	summary: any = null;

	// Rows for the line-item table
	rows: any[] = [];
	filteredRows: any[] = [];
	marketFilter: string = 'ALL';
	statusFilter: string = 'ALL';
	showZeroCashRows: boolean = false;	// Change 1: default OFF — hide $0 payment-received rows
	includeTestBookings: boolean = false;	// Rule 0 filter: default OFF — exclude test bookings from compliance report

	marketOptions = [
		{ label: 'All markets', value: 'ALL' },
		{ label: 'Hawaii', value: 'HAWAII' },
		{ label: 'Utah', value: 'UTAH' }
	];

	statusOptions = [
		{ label: 'All statuses', value: 'ALL' },
		{ label: 'Active', value: 'ACTIVE' },
		{ label: 'Released', value: 'RELEASED' },
		{ label: 'Refunded', value: 'REFUNDED' },
		{ label: 'Canceled', value: 'CANCELED' }
	];

	// Row-expansion state for the per-reservation calculation breakdown panel
	expandedRows: { [key: string]: boolean } = {};

	// Optional: incoming highlight from payment-mapping cross-link
	highlightResId: number | null = null;

	constructor(
		private http: HttpClient,
		private router: Router,
		private route: ActivatedRoute,
		private messageService: MessageService) {
	}

	gotoAudit(): void {
		this.router.navigate(['/escrow-report/audit']);
	}

	/** Navigate to the payment-mapping view, optionally pre-filtered to a single reservation. */
	gotoPaymentMapping(reservationId?: number): void {
		const queryParams = reservationId ? { reservationId } : {};
		this.router.navigate(['/escrow-report/payment-mapping'], { queryParams });
	}

	formatNumber(n: any): string {
		if (n == null) return '0';
		const v = typeof n === 'number' ? n : Number(n);
		if (isNaN(v)) return '0';
		return v.toLocaleString('en-US');
	}

	bankTrailClass(status: string): string {
		switch (status) {
			case 'IN_ESCROW': return 'trail-pill trail-ok';
			case 'NO_BANK_TRAIL': return 'trail-pill trail-bad';
			case 'MIXED': return 'trail-pill trail-warn';
			case 'NO_PAYMENTS': return 'trail-pill trail-neutral';
			default: return 'trail-pill';
		}
	}

	bankTrailLabel(status: string): string {
		switch (status) {
			case 'IN_ESCROW': return 'In escrow';
			case 'NO_BANK_TRAIL': return 'No bank trail';
			case 'MIXED': return 'Mixed';
			case 'NO_PAYMENTS': return 'No payments';
			case 'ESCROW': return 'Escrow';
			case 'OPERATING': return 'Operating';
			default: return status || 'Unknown';
		}
	}

	ngOnInit(): void {
		// Check if we arrived via cross-link from payment-mapping with a highlight request
		const hi = this.route.snapshot.queryParamMap.get('highlight');
		this.highlightResId = hi ? +hi : null;

		// Default as-of = today at midnight (CLAUDE.md: never bare new Date for date-only)
		const now = new Date();
		this.asOfDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		this.loadReport();
	}

	loadReport(): void {
		this.formLoading = true;
		const asOfStr = moment(this.asOfDate).format('YYYY-MM-DD');
		const url = `${environment.apiUrl}/escrow/report?asOfDate=${asOfStr}&recentDays=${this.recentDays}&includeTestBookings=${this.includeTestBookings}`;
		this.http.get<ApiResponse>(url).subscribe({
			next: (res) => {
				try {
					const data = (res && res.data && (res.data as any).escrowReport) ? (res.data as any).escrowReport : null;
					if (data) {
						this.summary = data;
						this.rows = data.rows || [];
						this.applyFilters();
						this.messageService.add({ severity: 'success', summary: 'Loaded', detail: `${this.rows.length} reservations`, life: 2500 });
					} else {
						this.summary = null;
						this.rows = [];
						this.filteredRows = [];
					}
				} catch (e) {
					console.error('escrow-report parse error', e);
				}
				this.formLoading = false;
			},
			error: (err) => {
				console.error('escrow-report load error', err);
				this.messageService.add({ severity: 'error', summary: 'Error', detail: err.message || 'Could not load escrow report' });
				this.formLoading = false;
			}
		});
	}

	applyFilters(): void {
		this.filteredRows = (this.rows || []).filter(r => {
			// Change 1: hide $0 payment-received rows by default
			if (!this.showZeroCashRows) {
				const cash = r.netCashHeld != null ? Number(r.netCashHeld) : 0;
				if (cash <= 0) return false;
			}
			if (this.marketFilter !== 'ALL' && r.market !== this.marketFilter) {
				return false;
			}
			// Change 3: lifecycle-based status filter (maps UI value to derived lifecycle label)
			if (this.statusFilter !== 'ALL') {
				const lc = this.deriveLifecycle(r);
				if (this.statusFilter === 'CANCELED') {
					if (!lc.startsWith('CANCELED')) return false;
				} else if (lc !== this.statusFilter) {
					return false;
				}
			}
			return true;
		});
	}

	// Change 3: derive accurate lifecycle label from reservation data
	deriveLifecycle(row: any): string {
		const status = row.reservationStatus || '';
		const netCash = row.netCashHeld != null ? Number(row.netCashHeld) : 0;
		const now = new Date();
		const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

		if (status === 'OX' || status === 'CN' || status === 'CL') {
			if (netCash > 0) return 'CANCELED — REFUND PENDING';
			return 'CANCELED — CLEARED';
		}
		if (row.checkOutDate) {
			const checkOut = new Date(row.checkOutDate);
			const checkOutDay = new Date(checkOut.getFullYear(), checkOut.getMonth(), checkOut.getDate());
			if (checkOutDay < today) return 'POST-CHECKOUT';
		}
		if (row.checkInDate) {
			const checkIn = new Date(row.checkInDate);
			const checkInDay = new Date(checkIn.getFullYear(), checkIn.getMonth(), checkIn.getDate());
			if (checkInDay <= today) return 'IN-STAY';
		}
		return 'ACTIVE';
	}

	// Change 3: CSS class for lifecycle pill
	lifecycleClass(row: any): string {
		const lc = this.deriveLifecycle(row);
		if (lc === 'CANCELED — REFUND PENDING') return 'status-pill status-canceled-refund';
		if (lc === 'CANCELED — CLEARED') return 'status-pill status-canceled-cleared';
		if (lc === 'IN-STAY') return 'status-pill status-in-stay';
		if (lc === 'POST-CHECKOUT') return 'status-pill status-post-checkout';
		return 'status-pill status-active';
	}

	// Change 3: tooltip text for lifecycle pill
	lifecycleTooltip(row: any): string {
		const lc = this.deriveLifecycle(row);
		if (lc === 'CANCELED — REFUND PENDING') {
			const days = this.daysSinceLastChange(row);
			return days != null ? `Canceled with cash held (${days} days since last change)` : 'Canceled with cash held';
		}
		return lc;
	}

	daysSinceLastChange(row: any): number | null {
		if (!row.lastEscrowChangeDate) return null;
		const last = new Date(row.lastEscrowChangeDate);
		const now = new Date();
		const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		const lastDay = new Date(last.getFullYear(), last.getMonth(), last.getDate());
		return Math.round((today.getTime() - lastDay.getTime()) / (1000 * 60 * 60 * 24));
	}

	onAsOfChange(): void {
		// Strip time to keep date-only semantics
		if (this.asOfDate) {
			const d = this.asOfDate;
			this.asOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
		}
		this.loadReport();
	}

	statusClass(status: string): string {
		if (status === 'SHORTFALL') return 'tile tile-shortfall';
		if (status === 'OVERAGE') return 'tile tile-overage';
		if (status === 'COMPLIANT') return 'tile tile-compliant';
		return 'tile';
	}

	formatCurrency(v: any): string {
		if (v == null) return '$0.00';
		const n = typeof v === 'number' ? v : Number(v);
		if (isNaN(n)) return '$0.00';
		return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
	}

	formatRatio(v: any): string {
		if (v == null) return '';
		const n = typeof v === 'number' ? v : Number(v);
		if (isNaN(n)) return '';
		return (n * 100).toFixed(2) + '%';
	}

	qboReconClass(): string {
		const status = this.summary?.qboReconStatus;
		if (status === 'MATCH') return 'tile tile-compliant';
		if (status === 'VARIANCE') return 'tile tile-shortfall';
		return 'tile';
	}

	formatDate(d: any): string {
		if (!d) return '';
		return moment(d).format('MM/DD/YYYY');
	}

	downloadExcel(): void {
		if (!this.filteredRows || this.filteredRows.length === 0) {
			this.messageService.add({ severity: 'warn', summary: 'No data', detail: 'Nothing to export' });
			return;
		}
		const flat = this.filteredRows.map(r => ({
			confirmationNumber: r.confirmationNumber,
			guestName: r.guestName,
			propertyCode: r.propertyCode,
			market: r.market,
			reservationStatus: r.reservationStatus,
			checkInDate: r.checkInDate ? moment(r.checkInDate).format('YYYY-MM-DD') : '',
			checkOutDate: r.checkOutDate ? moment(r.checkOutDate).format('YYYY-MM-DD') : '',
			totalNights: r.totalNights,
			nightsRemaining: r.nightsRemaining,
			totalBilled: r.totalBilled,
			escrowEligibleBilled: r.escrowEligibleBilled,
			// Change 2: renamed from netCashHeld to "Payment Received" in export
			'Payment Received': r.netCashHeld,
			allocatedNetCash: r.allocatedNetCash,
			escrowEligibleCash: r.escrowEligibleCash,
			perNightEscrow: r.perNightEscrow,
			releasedToDate: r.releasedToDate,
			requiredInEscrow: r.requiredInEscrow,
			exception: r.exception ? 'Y' : 'N',
			// Change 3: use derived lifecycle, not the raw status field
			lifecycle: this.deriveLifecycle(r),
			lastEscrowChangeDate: r.lastEscrowChangeDate ? moment(r.lastEscrowChangeDate).format('YYYY-MM-DD') : ''
		}));
		this.importExportComponent.exporData('excel', flat, 'escrow-report-' + moment(this.asOfDate).format('YYYY-MM-DD'));
	}

	downloadPdf(): void {
		this.formLoading = true;
		const asOfStr = moment(this.asOfDate).format('YYYY-MM-DD');
		const url = `${environment.apiUrl}/escrow/report/pdf?asOfDate=${asOfStr}&recentDays=${this.recentDays}`;
		this.http.get(url, { responseType: 'blob' }).subscribe({
			next: (blob: Blob) => {
				FileSaver.saveAs(blob, 'escrow-report-' + asOfStr + '.pdf');
				this.formLoading = false;
				this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'PDF downloaded', life: 2500 });
			},
			error: (err) => {
				console.error('escrow-report pdf error', err);
				this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not download PDF' });
				this.formLoading = false;
			}
		});
	}
}
